import { Button, Icon, IconButton, Input, Typography } from '@smartgift/frontend-toolkit';
import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';

import { HorizontalDivider } from '@hero/atoms';
import { DEVICE } from '@hero/constants';

import VerticalListItem from '../Product/VerticalList/Item';

const InputArea = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  position: absolute;
  width: 100%;
`;

const InputBackButton = styled(IconButton)`
  left: 8px;
  position: absolute;
  z-index: 2;
`;

const SearchInput = styled(Input)`
  width: 100%;

  input {
    padding-left: 40px !important;
  }
`;

const ProductSearch = styled(motion.form)`
  background: var(--color-bg-weaker);
  border-radius: var(--size-border-radius-md);
  bottom: 0;
  padding: 16px 0;
  position: absolute;
  right: 16px;
  top: 0;
  z-index: 2;

  ${({ isFocused }) => {
    return (
      isFocused &&
      css`
        display: block;
      `
    );
  }}
`;

const ProductSearchWrapper = styled.form`
  align-items: center;
  display: flex;
`;

const ProductSearchButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: var(--color-components-button-secondary-text-fg);
  cursor: pointer;
  display: flex;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
`;

const SearchList = styled.div`
  background-color: var(--color-bg-body);
  border: 1px solid var(--color-accent-secondary-weaker);
  border-radius: var(--size-border-radius-md);
  display: flex;
  flex-direction: column;
  font-size: 14px;
  gap: 8px;
  overflow: hidden;
  position: absolute;
  top: 43px;
  width: 100%;
  z-index: 4;
`;

const SearchListListItemText = styled.p`
  color: var(--color-neutral-800);
  max-width: calc(100% - 16px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SearchListListHeader = styled.div`
  align-items: center;
  color: var(--color-neutral-400);
  display: flex;
  font-weight: 600;
  justify-content: space-between;
  padding: 0 16px 8px;
`;

const SearchListList = styled.div`
  ${HorizontalDivider} {
    margin: 8px 16px;
  }
`;

const SearchListListItems = styled.div`
  max-height: 280px;
  overflow-y: auto;
  padding-bottom: 8px;
`;

const SearchListListItem = styled.div`
  align-items: center;
  color: var(--color-gray-25);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  line-height: 24px;

  ${Icon.S.Icon} {
    cursor: pointer;
    height: 8px;
    width: 8px;
  }

  :last-child {
    margin-bottom: 0;
  }

  padding: 4px 16px;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: var(--color-gray-50);
  }
`;

const SearchListInfo = styled.div`
  color: var(--color-neutral-400);
  cursor: default;
  display: block;
  padding: 8px 16px;
`;

const ResultsPanel = styled.div`
  display: flex;
  gap: 16px;
  padding: 16px;
  padding-left: 0;
  width: 40%;
`;

const ResultsMenu = styled.div`
  ${Button.S.Button} {
    height: 32px;
    margin-top: 8px;
    width: 120px;
  }
`;

const EmptyResultTitle = styled(Typography)`
  margin-bottom: 8px;

  ${DEVICE.mobile.max} {
    font-size: 16px;
    text-align: center;
  }
`;

const EmptyResultDescription = styled(Typography)`
  margin-bottom: 40px;
  text-align: center;
  width: 400px;

  ${DEVICE.mobile.max} {
    font-size: 14px;
    text-align: center;
  }
`;

const EmptyResult = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 24px;
  width: 100%;

  ${DEVICE.mobile.max} {
    flex-grow: 1;
    padding: 8px;
  }
`;

const ResultsContent = styled.div`
  display: flex;
  flex-grow: 1;
  flex-wrap: wrap;
  gap: 16px;
  max-height: 100%;
  overflow-y: auto;
  padding: 16px;
  width: 60%;

  ${Button.S.Button} {
    margin: 0 auto;
  }
`;

const Results = styled.div`
  background-color: var(--color-bg-body);
  border: 1px solid var(--color-accent-secondary-weaker);
  border-radius: var(--size-border-radius-md);
  display: flex;
  font-size: 14px;
  justify-content: space-between;
  max-height: 320px;
  overflow: hidden;
  position: absolute;
  top: 43px;
  width: 100%;
  z-index: 5;

  ${DEVICE.mobile.max} {
    flex-direction: column;

    ${ResultsPanel} {
      max-width: 100%;
      order: -1;
      width: 100%;
    }

    ${ResultsMenu} {
      align-items: center;
      display: flex;
      flex-grow: 1;
      justify-content: space-between;
      width: 100%;
    }

    ${ResultsContent} {
      width: 100%;
    }
  }

  ${DEVICE.mobile.min} {
    ${ResultsMenu} {
      flex-direction: column;
    }
  }
`;

const ResultsItems = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fill, minmax(256px, 1fr));
  width: 100%;

  ${VerticalListItem.S.VerticalListItem} {
    width: 100%;
  }

  ${DEVICE.mobile.max} {
    grid-template-columns: 1fr;
  }
`;

const ResultsMenuHeader = styled.div``;

const NoResentSearch = styled.p`
  color: var(--color-neutral-400);
  margin-top: -8px;
  padding: 0 16px 8px 16px;
`;

export {
  EmptyResult,
  EmptyResultDescription,
  EmptyResultTitle,
  InputArea,
  InputBackButton,
  NoResentSearch,
  ProductSearch,
  ProductSearchButton,
  ProductSearchWrapper,
  Results,
  ResultsContent,
  ResultsItems,
  ResultsMenu,
  ResultsMenuHeader,
  ResultsPanel,
  SearchInput,
  SearchList,
  SearchListInfo,
  SearchListList,
  SearchListListHeader,
  SearchListListItem,
  SearchListListItemText,
  SearchListListItems
};
